// @flow
import { filter } from "graphql-anywhere";
import { attempt, get, some } from "lodash/fp";
import tvgConf from "@tvg/conf";
import type { RaceOdds, BettingInterest, RaceProgram } from "@tvg/types/Race";
import type { OperationComponent } from "@apollo/client";
import BettingInterests from "./fragments/BettingInterestsSchema.graphql";
import TimeAndStatus from "./fragments/TimeAndStatus.graphql";
import Results from "./fragments/Results.graphql";
// $FlowFixMe
import WillPays from "./fragments/WillPays.graphql";
import Track from "./fragments/Track.graphql";
import RaceDetails from "./fragments/RaceDetails.graphql";
import WagerTypes from "./fragments/WagerTypes.graphql";
import Video from "./fragments/Video.graphql";
import type { Props } from "../types";

const highlightedOptions = {
  ...tvgConf().graphContext(),
  productPromo: tvgConf().product,
  brandPromo: tvgConf().brand
};

const QUERY_VARIABLES = {
  trackAbbr: null,
  wagerProfile: "PORT-Generic",
  ...highlightedOptions
};

const POLL_INTERVAL = 15000;

const getDecimalOddsValue = ({ numerator, denominator }: RaceOdds) => {
  let decimal = denominator && numerator ? numerator / denominator : numerator;

  if (!decimal || isNaN(decimal)) {
    decimal = Number.MAX_VALUE;
  }
  return decimal;
};

const getLowestOdd = (arr: BettingInterest[]) => {
  let lowestOdd = Number.MAX_VALUE;
  let myOdd = Number.MAX_VALUE;

  for (let i = 0; i < arr.length; i += 1) {
    if (some((runner) => runner.scratched === false, arr[i].runners)) {
      myOdd = getDecimalOddsValue(arr[i].currentOdds);

      if (myOdd < lowestOdd) {
        lowestOdd = myOdd;
      }
    }
  }

  return lowestOdd;
};

const isFavorite = (currentOdds, lowestOdd) => {
  const odd = getDecimalOddsValue(currentOdds);
  return odd === lowestOdd && odd !== 99;
};

const isFavoriteBIScratched = (bettingInterests: BettingInterest[]) => {
  for (let i = 0; i < bettingInterests.length; i += 1) {
    if (bettingInterests[i].favorite) {
      const { runners } = bettingInterests[i];

      for (let j = 0; j < runners.length; j += 1) {
        if (!runners[j].scratched) {
          return false;
        }
      }
    }
  }
  return true;
};

const extratInfoFromRace = (processingRace) => {
  const raceInfo = {};

  if (processingRace.id) {
    raceInfo.id = processingRace.id;
  }
  if (processingRace.tvgRaceId) {
    raceInfo.tvgRaceId = processingRace.tvgRaceId;
  }
  if (processingRace.raceNumber) {
    raceInfo.number = processingRace.raceNumber;
  }
  // Handle results
  attempt(() => {
    const results = filter(Results, processingRace);
    Object.assign(raceInfo, results);
  });

  // Handle willPays
  attempt(() => {
    const willPays = filter(WillPays, processingRace);
    Object.assign(raceInfo, willPays);
  });

  // Handle bettingInterests
  attempt(() => {
    const bettingInterests = filter(BettingInterests, processingRace);
    Object.assign(raceInfo, bettingInterests);
  });

  // Handle timeAndStatus
  attempt(() => {
    const timeAndStatus = filter(TimeAndStatus, processingRace);
    Object.assign(raceInfo, timeAndStatus);
  });

  // Handle track
  attempt(() => {
    const track = filter(Track, processingRace);
    Object.assign(raceInfo, track);
  });

  // Handle race
  attempt(() => {
    const race = filter(RaceDetails, processingRace);
    Object.assign(raceInfo, race);
  });

  // Handle wagerTypes
  attempt(() => {
    const wagerTypes = filter(WagerTypes, processingRace);
    Object.assign(raceInfo, wagerTypes);
  });

  // Handle video
  attempt(() => {
    const video = filter(Video, processingRace);
    Object.assign(raceInfo, video);
  });

  // Handle favorites
  attempt(() => {
    const bettingInterests = get("bettingInterests", raceInfo);
    if (bettingInterests) {
      const useLowestOddFavorite = isFavoriteBIScratched(bettingInterests);

      const newBettingInterests = bettingInterests.map((bettingInterest) => {
        if (bettingInterest.runners.length) {
          if (useLowestOddFavorite) {
            const lowestOdd = getLowestOdd(bettingInterests);

            return Object.assign(bettingInterest, {
              isFavorite: isFavorite(bettingInterest.currentOdds, lowestOdd)
            });
          }
          return Object.assign(bettingInterest, {
            isFavorite: bettingInterest.favorite
          });
        }
        return bettingInterest;
      });
      Object.assign(raceInfo, { bettingInterests: newBettingInterests });
    }
  });

  // Handle race open for betting
  attempt(() => {
    if (get("status.code", processingRace)) {
      const wagerable =
        ["SK", "C", "RO"].indexOf(get("status.code", processingRace)) < 0;
      Object.assign(raceInfo, { wagerable });
    }
  });

  // Handle Talent Picks
  attempt(() => {
    const talentPicksCounter = processingRace.talentPicks
      ? { talentPicksCounter: processingRace.talentPicks.length }
      : {};
    Object.assign(raceInfo, talentPicksCounter);
  });

  // Handle race promos
  attempt(() => {
    if (get("promos", processingRace)) {
      const promo = {
        isAboveTheLine: get("promos[0].isAboveTheLine", processingRace),
        promoPath: get("promos[0].promoPath", processingRace),
        rootParentPromoID: get("promos[0].rootParentPromoID", processingRace),
        isOptedIn: false,
        isPromoTagShown: get("promos[0].isPromoTagShown", processingRace)
      };

      Object.assign(raceInfo, { promo });
      Object.assign(raceInfo, { promos: get("promos", processingRace, []) });
    }
  });

  return raceInfo;
};

const getRaces = (result): RaceProgram[] =>
  (get("data.races", result) || []).map((race) => extratInfoFromRace(race));

const getLiveTvgRaces = (result): RaceProgram[] => {
  // $FlowFixMe
  const liveTvg = get("data.onTvg", result) || [];
  const liveTvg2 = get("data.onTvg2", result) || [];
  const liveTvgRaces = liveTvg.concat(liveTvg2);

  return liveTvgRaces.reduce((acc, race) => {
    const processingRace = extratInfoFromRace(race);

    // push race to races arrays
    if (["C", "RO"].indexOf(get("status.code", processingRace)) < 0) {
      acc.push(processingRace);
    }
    return acc;
  }, []);
};

const stopPolling = (result) => {
  let shouldStop = false;
  try {
    // if some race is not RO or with result we should not stop polling
    shouldStop =
      !result.data.races.some((race) => race.status.code !== "RO") &&
      !result.data.races.some((race) => !race.results);
  } catch (e) {
    shouldStop = false;
  }

  return shouldStop;
};

export const LiveTvgOptions = {
  skip: (props: Props) =>
    !get("shouldUpdate", props) ||
    (!get("liveTvg", props) && !get("liveTvg2", props)),
  options: (props: Props) => {
    const variables = {
      ...QUERY_VARIABLES,
      wagerProfile: get("wagerProfile", props) || "",
      liveTvg: get("liveTvg", props) || false,
      liveTvg2: get("liveTvg2", props) || false
    };

    return {
      pollInterval: POLL_INTERVAL * 8,
      fetchPolicy: "cache-and-network",
      ssr: false,
      variables
    };
  },
  props: (result: OperationComponent<Response>) => {
    if (stopPolling(result)) {
      result.data.stopPolling();
    } else {
      result.data.startPolling(POLL_INTERVAL * 8);
    }

    return {
      liveTvgRaces: getLiveTvgRaces(result),
      isLoadingLiveTvg:
        result.data.networkStatus === 1 || result.data.networkStatus === 2
    };
  }
};

export const ProbableOptions = {
  skip: (props: Props) => !get("shouldUpdate", props),
  options: (props: Props) => {
    const variables = {
      trackAbbr: (get("trackAbbr", props) || "").toUpperCase(),
      wagerProfile: get("wagerProfile", props) || ""
    };

    const poll = get("hasWebSockets", props)
      ? {}
      : { pollInterval: POLL_INTERVAL };

    return {
      pollInterval: 0,
      fetchPolicy: "cache-first",
      ssr: false,
      variables
    };
  },
  props: (result: OperationComponent<Response>) => {
    return {
      racesWithProbables: result?.data?.races,
      subscribeToMoreProbables:
        get("ownProps.hasWebSockets", result) && !result.data.loading
          ? result.data.subscribeToMore
          : null,
      isLoadingProbables:
        result.data.networkStatus === 1 || result.data.networkStatus === 2
    };
  }
};

export default {
  skip: (props: Props) => !get("shouldUpdate", props),
  options: (props: Props) => {
    const variables = {
      ...QUERY_VARIABLES,
      trackAbbr: (get("trackAbbr", props) || "").toUpperCase(),
      wagerProfile: get("wagerProfile", props) || ""
    };

    return {
      pollInterval: 0,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      ssr: false,
      variables
    };
  },
  props: (result: OperationComponent<Response>) => {
    const races = result.data.loading
      ? getRaces({ data: get("data.previousData", result) })
      : getRaces(result);

    const stayLoading = races.length
      ? get("ownProps.trackAbbr", result) !== get("[0].track.trackCode", races)
      : false;

    return {
      races,
      subscribeToMore:
        get("ownProps.hasWebSockets", result) && !result.data.loading
          ? result.data.subscribeToMore
          : null,
      isLoading: result.data.loading || stayLoading
    };
  }
};
